import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Box, CircularProgress } from '@mui/material';
import toast from 'react-hot-toast';
import { WizardStep } from '../common';
import { useCreatePreHiringPositionMutation } from '../../../../../api/preHQ';

const FormActions = ({
    onSaveAsDraft,
    isSaving,
    activeStep,
    setActiveStep,
    formValidate,
    formTriggerValidation,
    form,
    onSubmit,
}) => {
    const STEP_ORDER = [
        WizardStep.POSITION_DETAILS,
        WizardStep.TALENT_INSIGHTS,
        WizardStep.JOB_DESCRIPTION,
        WizardStep.PREVIEW_SUBMIT,
    ];
    const currentStepIndex = STEP_ORDER.indexOf(activeStep);
    const navigate = useNavigate();
    const [createPosition] = useCreatePreHiringPositionMutation();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleNext = async () => {
        // For Position Details step, validate mandatory fields
        if (activeStep === WizardStep.POSITION_DETAILS) {
            const formData = form.getValues();

            const isValid = await formTriggerValidation(['jobTitle', 'department', 'minYearsExp', 'maxYearsExp']);
            if (!isValid) {
                return; // Stop here if validation fails
            }
            // Check if min years exp is less than max years exp (only if both values are entered by user)
            if (formData.minYearsExp > 0 && formData.maxYearsExp > 0 && formData.minYearsExp > formData.maxYearsExp) {
                toast.error('Minimum years of experience must be less than maximum years of experience');
                return;
            }

            // Check if budget values are in ascending order (only if all values are entered by user)
            if (formData.budgetLowerLimit > 0 && formData.budgetMidLimit > 0 && formData.budgetUpperLimit > 0) {
                if (
                    formData.budgetLowerLimit >= formData.budgetMidLimit ||
                    formData.budgetMidLimit >= formData.budgetUpperLimit
                ) {
                    toast.error('Budget values must be in ascending order: Lower < Mid < Higher');
                    return;
                }
            }
        }

        // For Talent Insights step, validate skills
        else if (activeStep === WizardStep.TALENT_INSIGHTS) {
            const isValid = await formTriggerValidation(['mandatorySkills']);
            if (!isValid) {
                return; // Stop here if validation fails
            }
        }

        // For Job Description step, validate description
        else if (activeStep === WizardStep.JOB_DESCRIPTION) {
            const isValid = await formTriggerValidation([]);
            if (!isValid) {
                return; // Stop here if validation fails
            }
        }

        if (currentStepIndex < STEP_ORDER.length - 1) {
            setActiveStep(STEP_ORDER[currentStepIndex + 1]);
        } else {
            setIsSubmitting(true);
            try {
                const formData = form.getValues();
                const payload = {
                    title: formData.jobTitle,
                    role: formData.department,
                    locationId: formData.location?.id,
                    hiringManagerId: formData.hiringManager?.id,
                    budgetUpperLimit: formData.budgetUpperLimit,
                    budgetLowerLimit: formData.budgetLowerLimit,
                    budgetMidLimit: formData.budgetMidLimit,
                    employmentType: formData.employmentType,
                    mandatorySkills: formData.mandatorySkills.join(','),
                    optionalSkills: formData.optionalSkills.join(','),
                    description: formData.description,
                    minExperience: formData.minYearsExp,
                    maxExperience: formData.maxYearsExp,
                };

                await createPosition(payload).unwrap();
                setIsSubmitting(false);
                toast.success('Created successfully');
                setTimeout(() => {
                    navigate('/client/positions');
                }, 1000);
            } catch (error) {
                setIsSubmitting(false);
                console.error('Error creating position:', error);
                toast.error('Error creating position. Please try again.');
            }
        }
    };

    const handleBack = () => {
        if (currentStepIndex > 0) {
            setActiveStep(STEP_ORDER[currentStepIndex - 1]);
        }
    };

    const isFirstStep = activeStep === WizardStep.POSITION_DETAILS;
    const isLastStep = activeStep === WizardStep.PREVIEW_SUBMIT;

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                pt: 3,
                borderTop: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Box sx={{ display: 'flex', gap: 1.5 }}>
                {!isFirstStep && (
                    <Button type="button" variant="outlined" onClick={handleBack}>
                        Back
                    </Button>
                )}
                <Button
                    type={isLastStep ? 'submit' : 'button'}
                    onClick={handleNext}
                    disabled={isSaving || isSubmitting}
                    variant="contained"
                    sx={{
                        bgcolor: isSubmitting ? 'grey.400' : 'primary.main',
                        '&:hover': {
                            bgcolor: isSubmitting ? 'grey.400' : 'primary.dark',
                        },
                    }}
                >
                    {isSubmitting ? 'Submitting...' : isLastStep ? 'Submit' : 'Next'}
                    {isSubmitting && <CircularProgress size={16} sx={{ ml: 1, color: 'white' }} />}
                </Button>
            </Box>
        </Box>
    );
};

export default FormActions;
